<template>
  <base-slide-over ref="slideover" title="Edit MSRP" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoading">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/4">
          <base-skeleton-loader class="tw-h-3 tw-w-full" />
        </div>
        <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        <div class="tw-flex-grow">
          <base-skeleton-loader class="tw-h-10 tw-w-full" />
        </div>
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div class="tw-grid tw-grid-cols-1 tw-gap-y-6">
        <div v-for="row in orderedUiRows" :key="row.name" class="tw-flex tw-items-center tw-gap-x-4 tw-border-b tw-pb-4">
          <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex-col">
              <div class="tw-flex-col tw-my-auto tw-font-normal">
                {{ row.name }}
              </div>
            </div>
            <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
          </div>

          <div class="tw-flex-grow">
            <base-currency-input label="MSRP" :int-value.sync="row.msrp" v-model="row.msrpDisplay" :precision="2" />
          </div>
        </div>
      </div>
    </div>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" :disabled="isSaving" @click="hide()">
          Cancel
        </base-button>
        <base-button :loading="isSaving" class="tw-w-auto" @click="save()">
          Save
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";

export default {
  name: "BusinessProgramMsrpSlideover",
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      programsData: [],
      uiRows: [],
    };
  },
  computed: {
    orderedUiRows() {
      return orderBy(this.uiRows, ["name"], ["asc"]);
    },
  },
  methods: {
    async show() {
      this.resetData();
      this.$refs.slideover.show();
      await this.loadPrograms();
    },

    hide() {
      this.$refs.slideover.hide();
      this.resetData();
    },

    resetData() {
      this.isLoading = false;
      this.isSaving = false;
      this.programsData = [];
      this.uiRows = [];
    },

    async loadPrograms() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);

        this.programsData = response.data.map((program) => ({
          ...program,
          msrp: program.msrp || 0,
          msrpDisplay: program.msrp || 0,
        }));

        this.buildUiRowsFromProgramsData();
      } catch (error) {
        console.error("Error fetching programs:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load programs",
          message: "There was an issue fetching program data. Please try again.",
          error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    buildUiRowsFromProgramsData() {
      const tempUiRows = {};

      this.programsData.forEach((program) => {
        if (!program.programGroup) return;

        if (!tempUiRows[program.name]) {
          tempUiRows[program.name] = {
            name: program.name,
            msrp: program.msrp || 0,
            msrpDisplay: program.msrp || 0,
            programGroupInputs: [],
          };
        }

        tempUiRows[program.name].programGroupInputs.push(program);
      });

      this.uiRows = Object.values(tempUiRows).filter((row) => row.programGroupInputs.length > 0);
    },

    async save() {
      this.isSaving = true;

      const updates = [];

      // Apply the single MSRP value to all program groups with the same name
      this.uiRows.forEach((row) => {
        row.programGroupInputs.forEach((program) => {
          updates.push({
            businessProgramId: program.businessProgramId,
            msrp: row.msrp, // Use the single MSRP from the row
          });
        });
      });

      try {
        await this.$axios.patch(`/businesses/${this.businessId}/programs/msrp`, {
          programs: updates,
        });

        this.$emit("save");
        this.hide();
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "MSRP Updated",
          message: `Program MSRP updated successfully.`,
        });
      } catch (error) {
        console.error("Error saving MSRP:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save MSRP",
          message: `There was an issue saving the MSRP. Please try again.`,
          error,
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
